import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Skeleton } from '@mui/material';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../kanban-board/crm-theme';
import { formatCrmDateTime } from '../kanban-board/formatters/crm-datetime';
import { CrmLeadService } from '../kanban-board/services/crm-lead.service';
import {
    ELeadOutcome, LeadDetailResponse,
} from '../kanban-board/services/responses/lead.response';
import { CrmLossReasonModal } from '../kanban-board/components/modals/crm-loss-reason-modal';
import { CrmWonValueModal } from '../kanban-board/components/modals/crm-won-value-modal';
import { LeadHeader } from './components/lead-header';
import { TabBar } from '@components/TabBar';
import { LeadBlock, LeadField } from './components/lead-block';
import { LeadHistoryWide } from './components/lead-history-wide';
import { LeadNote } from './components/lead-note';
import { LeadActions } from './components/lead-actions';
import { LeadDayAgenda } from './components/lead-day-agenda';
import { LeadDealModal, formatDateOnly } from './components/lead-deal-modal';
import { LeadAttachments } from './components/lead-attachments';
import { LeadPayments } from './components/lead-payments';
import { LeadCustomFields } from './components/lead-custom-fields';
import { useCustomFields } from '@layouts/pages/Settings/general/custom-fields/hooks/use-custom-fields.hook';
import { sourceLabel } from '../kanban-board/formatters/lead-source-label';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { OUTCOME_SAVED } from '../kanban-board/formatters/crm-outcome-message';

export const LeadPage = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    const { leadId } = useParams<{ leadId: string }>();
    const { fields: customFields } = useCustomFields();

    const [detail, setDetail] = useState<LeadDetailResponse | null>(null);
    const [askingLoss, setAskingLoss] = useState(false);
    const [askingWin, setAskingWin] = useState(false);
    const [editingDeal, setEditingDeal] = useState(false);

    // Abre nas ações: é o que cobra trabalho, e a pendência atrasada aparece sem procurar.
    const [tab, setTab] = useState<'acoes' | 'historico' | 'detalhes'>('acoes');
    const [suggestedTime, setSuggestedTime] = useState<string>();

    // Sobe a cada mudança para o histórico e as ações recarregarem, sem cada bloco saber dos outros.
    const [version, setVersion] = useState(0);

    const load = useCallback(async () => {
        if (!leadId) return;

        try {
            const res = await new CrmLeadService().detail(leadId);
            setDetail(res.data.data);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
            navigate('/crm/quadro-kanban');
        }
    }, [leadId, navigate, showAlert]);

    useEffect(() => {
        load();
    }, [load]);

    const reload = () => {
        load();
        setVersion((v) => v + 1);
    };

    /// O datetime-local espera hora local sem fuso; ISO com Z deixaria o campo vazio.
    const chooseTime = (when: Date) => {
        const local = new Date(when.getTime() - when.getTimezoneOffset() * 60000);

        setSuggestedTime(local.toISOString().slice(0, 16));
    };

    const move = async (stageId: string) => {
        if (!detail) return;

        try {
            await new CrmLeadService().move(detail.lead.id, {
                targetColumnId: stageId,
                targetSortOrder: 0,
            });
            reload();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    const mark = async (
        outcome: ELeadOutcome,
        extra?: { lossReasonId?: string | null; lossNote?: string | null; closedValue?: number | null }
    ) => {
        if (!detail) return;

        try {
            await new CrmLeadService().setOutcome(detail.lead.id, { outcome, ...extra });
            showAlert(t(OUTCOME_SAVED[outcome]), 'success', 2000);
            reload();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    if (!detail) {
        return (
            <DashboardLayout>
                <DashboardNavbar title={t('telaLead.titulo')} />
                <Box sx={{ p: 2 }}>
                    <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
                    <Skeleton variant="rounded" height={340} />
                </Box>
            </DashboardLayout>
        );
    }

    const { lead, stages, summary } = detail;

    const money = (v?: number | null) =>
        v == null ? undefined : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    return (
        <DashboardLayout>
            <DashboardNavbar title={lead.contactName} />

            <Box sx={{ background: cc.pageBg, borderRadius: 3, p: 2 }}>
                <LeadHeader
                    lead={lead}
                    company={detail.company}
                    onWin={() => setAskingWin(true)}
                    onLose={() => setAskingLoss(true)}
                    onReopen={() => mark(ELeadOutcome.Open)}
                    onChanged={reload}
                    stages={stages}
                    onMove={move}
                />

                <Box sx={{ mt: 2 }}>
                <TabBar
                    current={tab}
                    onChange={setTab}
                    tabs={[
                        {
                            value: 'acoes',
                            label: t('telaLead.acoes'),
                            count: summary.openActions,
                        },
                        { value: 'historico', label: t('telaLead.historico') },
                        { value: 'detalhes', label: t('telaLead.detalhes') },
                    ]}
                />
                </Box>

                {tab === 'acoes' && (
                    <Box
                        sx={{
                            display: 'grid',
                            // minmax(0, …): descrição comprida quebra a linha em vez de alargar a coluna.
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 380px' },
                            gap: 1.5,
                            mt: 1.5,
                            // start, não stretch: esticado, o bloco de ações copiava a altura do calendário.
                            alignItems: 'start',
                        }}
                    >
                        <LeadActions
                            leadId={lead.id}
                            version={version}
                            onChanged={reload}
                            suggestedTime={suggestedTime}
                        />

                        <LeadDayAgenda
                            leadId={lead.id}
                            version={version}
                            onChooseTime={chooseTime}
                        />
                    </Box>
                )}

                {tab === 'historico' && (
                    <Box sx={{ mt: 1.5 }}>
                        <LeadHistoryWide
                            leadId={lead.id}
                            version={version}
                            onChanged={reload}
                        />
                    </Box>
                )}

                {/* Grade de verdade, não colunas empilhadas: blocos da mesma linha crescem juntos. */}
                <Box
                    sx={{
                        display: tab === 'detalhes' ? 'grid' : 'none',
                        gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
                        gap: 1.5,
                        mt: 1.5,
                        alignItems: 'stretch',
                    }}
                >
                    {/* Status e responsável já estão no cabeçalho: aqui fica só o do negócio. */}
                    <LeadBlock
                        title={t('telaLead.negocio')}
                        action={{
                            label: t('general.editar'),
                            icon: <EditOutlinedIcon sx={{ fontSize: '15px !important' }} />,
                            onClick: () => setEditingDeal(true),
                        }}
                    >
                        <LeadField label={t('crmKanban.procedimento')} value={lead.procedureName} />
                        <LeadField
                            label={t('crmKanban.valor')}
                            value={money(lead.value)}
                            highlight
                        />
                        <LeadField
                            label={t('telaLead.previsaoFechamento')}
                            value={formatDateOnly(lead.expectedCloseDate)}
                        />
                        {lead.closedValue != null && (
                            <LeadField
                                label={t('telaLead.valorFechado')}
                                value={money(lead.closedValue)}
                                highlight
                            />
                        )}
                        {lead.outcome === ELeadOutcome.Lost && (
                            <LeadField
                                label={t('telaLead.motivoPerda')}
                                value={lead.lossReasonName}
                            />
                        )}
                    </LeadBlock>

                    <LeadBlock title={t('telaLead.resumo')}>
                        <LeadField
                            label={t('telaLead.noFunilHa')}
                            value={t('telaLead.dias', { total: summary.daysInFunnel })}
                        />
                        <LeadField
                            label={t('telaLead.nestaEtapaHa')}
                            value={t('telaLead.dias', { total: summary.daysInStage })}
                        />
                        <LeadField
                            label={t('telaLead.ultimaMensagem')}
                            value={lead.lastMessageAt ? formatCrmDateTime(lead.lastMessageAt) : undefined}
                        />
                        <LeadField
                            label={t('telaLead.acoesConcluidas')}
                            value={`${summary.doneActions} / ${summary.doneActions + summary.openActions}`}
                        />
                    </LeadBlock>

                    <LeadBlock title={t('telaLead.origem')}>
                        <LeadField label={t('telaLead.canal')} value={sourceLabel(t, lead.source, lead.sourceDetail)} />
                        <LeadField label={t('telaLead.campanha')} value={lead.sourceCampaign} />
                        <LeadField
                            label={t('telaLead.criado')}
                            value={formatCrmDateTime(lead.createdAt)}
                        />
                    </LeadBlock>

                    <LeadCustomFields lead={lead} fields={customFields} onChanged={reload} />

                    <LeadNote lead={lead} onChanged={reload} />

                    <LeadAttachments
                        leadId={lead.id}
                        columns={3}
                        version={version}
                        signerName={lead.contactName}
                        signerEmail={lead.contactEmail}
                    />

                    <LeadPayments leadId={lead.id} columns={3} version={version} />
                </Box>

            </Box>

            <CrmLossReasonModal
                open={askingLoss}
                leadName={lead.contactName}
                leadId={lead.chatwootConversationId ? lead.id : null}
                onCancel={() => setAskingLoss(false)}
                onConfirm={(lossReasonId, lossNote) => {
                    setAskingLoss(false);
                    mark(ELeadOutcome.Lost, { lossReasonId, lossNote });
                }}
            />

            <LeadDealModal
                lead={lead}
                open={editingDeal}
                onClose={() => setEditingDeal(false)}
                onSaved={reload}
            />

            <CrmWonValueModal
                open={askingWin}
                leadName={lead.contactName}
                estimated={lead.value}
                onCancel={() => setAskingWin(false)}
                onConfirm={(closedValue) => {
                    setAskingWin(false);
                    mark(ELeadOutcome.Won, { closedValue });
                }}
            />
        </DashboardLayout>
    );
};
